import React, { useMemo, useState } from "react";
import { BarChart3, CheckCircle2, Clock, Lock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { toast } from "sonner";

function endsText(expiresAt, t) {
  if (!expiresAt) return null;
  const end = new Date(expiresAt).getTime();
  const now = Date.now();
  const ms = end - now;
  if (ms <= 0) return t("poll.closed");
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return t("poll.endsIn", { when: `${mins}m` });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("poll.endsIn", { when: `${hrs}h` });
  const days = Math.floor(hrs / 24);
  return t("poll.endsIn", { when: `${days}d` });
}

/**
 * Render a poll post: either a voting form (if user hasn't voted and poll is open)
 * or a results bar-chart (if they've voted or poll is closed/expired).
 * Keeps local state in sync with the server response.
 */
export default function PollCard({ post, onUpdate }) {
  const { user } = useAuth();
  const { t } = useT();
  const initialPoll = post.poll || { question: post.text, options: [], closed: false, expires_at: null };
  const [poll, setPoll] = useState(initialPoll);
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === "admin";
  const total = useMemo(() => (poll.options || []).reduce((s, o) => s + (o.votes?.length || 0), 0), [poll]);
  const expiredNow = poll.closed || (poll.expires_at && new Date(poll.expires_at).getTime() <= Date.now());
  const myVote = useMemo(() => {
    if (!user) return null;
    const o = (poll.options || []).find((opt) => (opt.votes || []).includes(user.id));
    return o ? o.id : null;
  }, [poll, user]);
  const showResults = !!myVote || expiredNow;
  const endsLabel = !expiredNow ? endsText(poll.expires_at, t) : t("poll.closed");

  const vote = async (optionId) => {
    if (!user || user === false) return toast.error(t("err.unauthorized"));
    if (busy || expiredNow) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/polls/${post.id}/vote`, { option_id: optionId });
      const next = { ...poll, options: data.options };
      setPoll(next);
      if (typeof onUpdate === "function") onUpdate(next);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const close = async () => {
    if (!window.confirm(t("poll.closeConfirm"))) return;
    setBusy(true);
    try {
      await api.post(`/polls/${post.id}/close`);
      const next = { ...poll, closed: true };
      setPoll(next);
      if (typeof onUpdate === "function") onUpdate(next);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`poll-${post.id}`} className="bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 border-l-[#7C3AED] p-4 mt-3">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 bg-[#7C3AED] text-white font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-0.5">
          <BarChart3 className="w-3 h-3" /> {t("poll.badge")}
        </span>
        {expiredNow ? (
          <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
            <Lock className="w-3 h-3" /> {t("poll.closed")}
          </span>
        ) : endsLabel ? (
          <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#EA580C]">
            <Clock className="w-3 h-3" /> {endsLabel}
          </span>
        ) : null}
        {isAdmin && !expiredNow && (
          <button
            type="button"
            onClick={close}
            disabled={busy}
            data-testid={`poll-close-${post.id}`}
            className="ml-auto font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] hover:text-[#E11D48] transition-colors disabled:opacity-50"
          >
            {busy ? t("poll.closing") : t("poll.close")}
          </button>
        )}
      </div>

      <div className="font-heading text-base sm:text-lg uppercase tracking-wide text-[#09090B] dark:text-[#F4F4F5] mb-3">
        {poll.question}
      </div>

      {!showResults ? (
        <div className="space-y-2" data-testid={`poll-options-${post.id}`}>
          {(poll.options || []).map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => vote(o.id)}
              disabled={busy}
              data-testid={`poll-vote-${post.id}-${o.id}`}
              className="w-full text-left border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-2.5 text-sm font-body text-[#09090B] dark:text-[#F4F4F5] hover:border-[#7C3AED] hover:bg-[#7C3AED]/5 transition-colors disabled:opacity-50"
            >
              {o.text}
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-2" data-testid={`poll-results-${post.id}`}>
          {(poll.options || []).map((o) => {
            const count = (o.votes || []).length;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            const mine = o.id === myVote;
            return (
              <div key={o.id} data-testid={`poll-result-${post.id}-${o.id}`} className="relative">
                <div className="relative overflow-hidden border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2">
                  <div
                    className={`absolute inset-y-0 left-0 ${mine ? "bg-[#7C3AED]/20" : "bg-[#EA580C]/10"} transition-all duration-500`}
                    style={{ width: `${pct}%` }}
                    aria-hidden
                  />
                  <div className="relative flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {mine && <CheckCircle2 className="w-3.5 h-3.5 text-[#7C3AED] flex-shrink-0" />}
                      <span className={`truncate text-sm font-body ${mine ? "text-[#7C3AED] font-medium" : "text-[#09090B] dark:text-[#F4F4F5]"}`}>
                        {o.text}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="font-mono text-[10px] text-[#71717A]">{count}</span>
                      <span className={`font-heading text-sm ${mine ? "text-[#7C3AED]" : "text-[#09090B] dark:text-[#F4F4F5]"}`}>{pct}%</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] pt-1">
            {t("poll.total", { n: total })}{myVote ? ` · ${t("poll.voted")}` : ""}
          </div>
        </div>
      )}
    </div>
  );
}
